// src/context/BetSlipContext.jsx
import React, { createContext, useState, useContext, useEffect } from 'react';

const BetSlipContext = createContext();

export const useBetSlip = () => {
  const context = useContext(BetSlipContext);
  if (!context) {
    throw new Error('useBetSlip must be used within a BetSlipProvider');
  }
  return context;
};

export const BetSlipProvider = ({ children }) => {
  const [bets, setBets] = useState([]);
  const [totalOdds, setTotalOdds] = useState(1);
  const [betslipCount, setBetslipCount] = useState(0);

  // Load bets from localStorage on mount
  useEffect(() => {
    const savedBets = JSON.parse(localStorage.getItem('betslip') || '[]');
    setBets(savedBets);
    setBetslipCount(savedBets.length);
  }, []);

  // Save bets to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('betslip', JSON.stringify(bets));
    setBetslipCount(bets.length);
    
    // Calculate total odds
    if (bets.length > 0) {
      const odds = bets.reduce((acc, bet) => acc * bet.odds, 1);
      setTotalOdds(odds);
    } else {
      setTotalOdds(1);
    }
  }, [bets]);

  const addToBetSlip = (bet) => {
    const betId = `${bet.matchId}-${bet.selection}-${Date.now()}`;
    const newBet = {
      id: betId,
      ...bet,
      addedAt: new Date().toISOString()
    };
    
    setBets(prev => {
      // Check if similar bet already exists
      const exists = prev.some(b => 
        b.matchId === bet.matchId && 
        b.selection === bet.selection
      );
      
      if (exists) {
        // Update existing bet
        return prev.map(b => 
          b.matchId === bet.matchId && b.selection === bet.selection 
            ? { ...b, ...bet, addedAt: new Date().toISOString() }
            : b
        );
      }
      
      return [...prev, newBet];
    });
    
    return true;
  };

  const removeFromBetSlip = (betId) => {
    setBets(prev => prev.filter(bet => bet.id !== betId));
  };

  const clearBetSlip = () => {
    setBets([]);
  };

  const updateBetStake = (betId, stake) => {
    setBets(prev => prev.map(bet => 
      bet.id === betId ? { ...bet, stake: parseFloat(stake) || 0 } : bet
    ));
  };

  const calculatePotentialWin = (stake) => {
    if (!stake || bets.length === 0) return 0;
    return parseFloat(stake) * totalOdds;
  };

  return (
    <BetSlipContext.Provider value={{
      bets,
      totalOdds,
      betslipCount,
      addToBetSlip,
      removeFromBetSlip,
      clearBetSlip,
      updateBetStake,
      calculatePotentialWin
    }}>
      {children}
    </BetSlipContext.Provider>
  );
};